import { QueryClientProvider } from '@tanstack/react-query';
import { Fragment, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { makeQueryClient } from '@/lib/queryClient';
import { usePersonPreviewStore } from '@/stores/personPreviewStore';

interface QueryProviderProps {
  children: ReactNode;
}

/**
 * The query cache, in two namespaces (CDEV-2234, MEMFAB-PERSON-PREVIEW-ADDENDUM
 * PP-7). The caller's own cache lives for the session. While a person is
 * previewed, every hook reads a separate cache made for that person, so no
 * query key anywhere can serve the caller's data as the previewed view or the
 * reverse. On exit the preview's cache is cancelled and discarded, and
 * anything the caller's cache took in after the preview began is read again.
 */
export function QueryProvider({ children }: QueryProviderProps) {
  const [realClient] = useState(makeQueryClient);
  const viewer = usePersonPreviewStore((s) => s.viewer?.iri ?? null);
  const enteredAt = usePersonPreviewStore((s) => s.enteredAt);
  const previewClient = useMemo(() => (viewer ? makeQueryClient() : null), [viewer]);
  const enteredAtRef = useRef(enteredAt);
  enteredAtRef.current = enteredAt;

  useEffect(() => {
    if (!previewClient) return undefined;
    const since = enteredAtRef.current ?? Date.now();
    // Nothing the caller's cache was fetching carries on into the preview.
    void realClient.cancelQueries();
    return () => {
      void previewClient.cancelQueries();
      previewClient.clear();
      void realClient.invalidateQueries({ predicate: (q) => q.state.dataUpdatedAt >= since });
    };
  }, [previewClient, realClient]);

  // The tree under the provider is remounted when the namespace changes: a
  // mounted useQuery keeps the client it was first given, so a hook mounted
  // before the switch would go on reading the other cache.
  return (
    <QueryClientProvider client={previewClient ?? realClient}>
      <Fragment key={viewer ?? 'self'}>{children}</Fragment>
    </QueryClientProvider>
  );
}

export default QueryProvider;
